import type { Frequency } from '@/shared/components/timeline-grid'

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlignJustifyIcon, CalendarIcon, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CreatePricingCampaign } from '@/features/pricing-campaign/create'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { MasterTable } from '@/shared/components/master-table'
import { SRT_TableToolbar } from '@/shared/components/master-table/components/tools/SRT_TableToolbar'
import { Button } from '@/shared/components/ui/button'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useMainScenarioId } from '@/shared/hooks/useMainScenarioId'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { useDrawers } from '@/shared/lib/drawer-manager'

import { usePricingCampaignsTable } from '../lib/usePricingCampaignsTable'
import {
	PricingCampaignsProvider,
	usePricingCampaigns,
} from '../providers/PricingCampaignsProvider'
import { PricingCampaignBulkActions } from './PricingCampaignBulkActions'
import { PricingCampaignsFilter } from './PricingCampaignsFilter'
import { PricingCampaignsTimeline } from './PricingCampaignsTimeline'

export function LocalPricingCampaigns() {
	const optimizationGroupId = useOptimizationGroupId()!
	const scenarioId = useMainScenarioId(optimizationGroupId)

	const scope = useMemo(
		() => ({
			optimization_group_id: optimizationGroupId!,
			scenario_id: scenarioId!,
		}),
		[optimizationGroupId, scenarioId]
	)

	return (
		<ErrorBoundaryWithUser showDialog>
			<PricingCampaignsProvider scope={scope}>
				<PricingCampaignsView />
			</PricingCampaignsProvider>
		</ErrorBoundaryWithUser>
	)
}

export function PricingCampaignsView() {
	const { t } = useTranslation()

	const {
		scope,
		filteredRows,
		setSelected,
		isLoading,
		staticFilter,
		setStaticFilter,
	} = usePricingCampaigns()
	const timelienRef = useRef<null | { scrollToCurrentDate: () => void }>(null)
	const { closeDrawer } = useDrawers()
	const [view, setView] = useState<string>('table')
	const [frequency, setFrequency] = useState<Frequency>('week')
	useEffect(() => () => {
		closeDrawer('PRICING_ALERTS')
	})
	useEffect(() => {
		view === 'timeline' && setSelected([])
	}, [view])
	const table = usePricingCampaignsTable({
		data: filteredRows,
		scope,
		isLoading,
	})

	return (
		<Tabs defaultValue='table' asChild onValueChange={setView} value={view}>
			<Layout className='relative flex h-full flex-1 flex-col gap-2 pt-2'>
				<Header
					className='flex items-center justify-between px-4'
					data-testid='pricing-campaigns-toolbar'
				>
					<div className='flex items-center gap-2'>
						<SRT_TableToolbar table={table} />
						<PricingCampaignsFilter />
						<ToggleGroup
							variant='outline'
							type='single'
							value={staticFilter}
							onValueChange={setStaticFilter}
						>
							<ToggleGroupItem
								className='flex-none px-4'
								value='all'
								aria-label='all'
							>
								{t('all', { defaultValue: 'All' })}
							</ToggleGroupItem>
							<ToggleGroupItem
								className='flex-none px-4'
								value='local'
								aria-label='local'
							>
								{t('local', { defaultValue: 'Local' })}
							</ToggleGroupItem>
							<ToggleGroupItem
								className='flex-none px-4'
								value='global'
								aria-label='global'
							>
								{t('global', { defaultValue: 'Global' })}
							</ToggleGroupItem>
						</ToggleGroup>
						{view === 'timeline' && (
							<TimelineFrequencySelector
								frequency={frequency}
								setFrequency={setFrequency}
								timelienRef={timelienRef}
							/>
						)}
					</div>
					<div className='flex items-center gap-2'>
						<PricingCampaignBulkActions />
						<Tooltip>
							<TooltipTrigger asChild>
								<span>
									<CreatePricingCampaign
										location='top-bar'
										scope={{ optimizationGroupId: scope.optimization_group_id }}
									>
										<Button
											variant='primary'
											size='icon'
											data-testid='create-pc'
										>
											<PlusIcon />
										</Button>
									</CreatePricingCampaign>
								</span>
							</TooltipTrigger>
							<TooltipContent>
								{t('pc.create.tooltip', {
									defaultValue: 'Create pricing campaign',
								})}
							</TooltipContent>
						</Tooltip>
						<TabsList>
							<TabsTrigger value='table'>
								<AlignJustifyIcon />
							</TabsTrigger>
							<TabsTrigger value='timeline'>
								<CalendarIcon />
							</TabsTrigger>
						</TabsList>
					</div>
				</Header>
				<TabsContent value='table' asChild>
					<Content className='overflow-hidden px-4 pb-4'>
						<div
							data-testid='pricing-campaigns-table'
							className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'
						>
							<MasterTable table={table} />
						</div>
					</Content>
				</TabsContent>
				<TabsContent value='timeline' asChild>
					<Content className='overflow-hidden px-4 pb-4'>
						<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
							<PricingCampaignsTimeline
								innerRef={timelienRef}
								frequency={frequency}
								data={table.getRowModel().flatRows.map((row) => row.original)}
								scope={scope}
							/>
						</div>
					</Content>
				</TabsContent>
			</Layout>
		</Tabs>
	)
}

const TimelineFrequencySelector = ({
	frequency,
	setFrequency,
	timelienRef,
}: {
	frequency: Frequency
	setFrequency: (value: Frequency) => void
	timelienRef: React.RefObject<{ scrollToCurrentDate: () => void } | null>
}) => {
	const { t } = useTranslation()

	const handleFrequencyClick = (value: Frequency) => {
		ampli.pcsTimelineFrequencyClick({
			value,
		})
		setFrequency(value)
	}

	return (
		<>
			<ToggleGroup
				variant='outline'
				type='single'
				value={frequency}
				onValueChange={setFrequency as (value: string) => void}
			>
				{(['week', 'month', 'quarter'] as Frequency[]).map(
					(item: Frequency) => (
						<Tooltip key={item}>
							<TooltipTrigger asChild>
								<ToggleGroupItem
									value={item}
									key={item}
									className='flex-none px-3'
									onClick={() => handleFrequencyClick(item)}
								>
									{item[0].toUpperCase()}
								</ToggleGroupItem>
							</TooltipTrigger>
							<TooltipContent>
								{t(`timeline.frequency.${item}.hint`)}
							</TooltipContent>
						</Tooltip>
					)
				)}
			</ToggleGroup>
			<Button onClick={() => timelienRef.current?.scrollToCurrentDate()}>
				{t('today', { defaultValue: 'Today' })}
			</Button>
		</>
	)
}
